<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisChecklistPanel from 'dashboard/components-next/LaisChecklistPanel.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  presentChecklist,
  availableItemActions,
} from '../utils/transactionChecklistPresenter';

/**
 * TransactionChecklistPanel — FE-AUD-76. Wraps the global LaisChecklistPanel
 * with the closing-transaction / documentation mapping. Emits a normalized
 * `item-action` ({ item, action }) so the host owns the API command (complete /
 * reopen / add_evidence / unblock); waiver is emitted separately because it
 * needs an authorized role + reason modal.
 */
const props = defineProps({
  transaction: { type: Object, default: null },
  offer: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  actionInFlight: { type: String, default: '' },
  actionError: { type: String, default: '' },
  referenceNow: { type: [String, Number, Date], default: undefined },
});

const emit = defineEmits(['itemAction']);

const { t } = useI18n();
const pendingAction = ref(null);
const actionReason = ref('');
const evidenceRef = ref('');
const validationError = ref('');

const model = computed(() =>
  presentChecklist({ transaction: props.transaction, offer: props.offer })
);

const actionLabel = action => {
  switch (action) {
    case 'complete':
      return t('PROPERTY_OFFERS.CHECKLIST.ACTIONS.COMPLETE');
    case 'reopen':
      return t('PROPERTY_OFFERS.CHECKLIST.ACTIONS.REOPEN');
    case 'unblock':
      return t('PROPERTY_OFFERS.CHECKLIST.ACTIONS.UNBLOCK');
    case 'add_evidence':
      return t('PROPERTY_OFFERS.CHECKLIST.ACTIONS.ADD_EVIDENCE');
    case 'waive':
      return t('PROPERTY_OFFERS.CHECKLIST.ACTIONS.WAIVE');
    default:
      return action;
  }
};

const blockerLabel = blocker => {
  switch (blocker) {
    case 'dependency':
      return t('PROPERTY_OFFERS.CHECKLIST.BLOCKERS.DEPENDENCY');
    case 'rejected':
      return t('PROPERTY_OFFERS.CHECKLIST.BLOCKERS.REJECTED');
    default:
      return '';
  }
};

// The presenter returns i18n descriptors instead of ready-made copy, so labels
// and the redacted evidence counter are translated here.
const resolveLabel = item => {
  if (item.label) return item.label;
  if (!item.labelKey) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(item.labelKey);
};

const resolveEvidenceLabel = evidence => {
  if (!evidence) return '';
  if (typeof evidence === 'string') return evidence;
  if (!evidence.key) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(evidence.key, { count: evidence.count });
};

const items = computed(() =>
  model.value.items.map(item => ({
    ...item,
    label: resolveLabel(item),
    evidenceLabel: resolveEvidenceLabel(item.evidenceLabel),
    actionLabel: item.actionKey ? actionLabel(item.actionKey) : '',
    blocker: blockerLabel(item.blocker),
  }))
);

const isPartial = computed(() => model.value.partial === true);
const hasTransaction = computed(() => model.value.source === 'transaction');
const progressLabel = computed(() =>
  t('PROPERTY_OFFERS.CHECKLIST.PROGRESS', { progress: model.value.progress })
);
const actionItems = computed(() =>
  items.value.map(item => ({
    ...item,
    actions: availableItemActions(item, {
      canWaive: model.value.permissions?.can_waive_item === true,
    }),
  }))
);
const installments = computed(() => model.value.installments || []);
const history = computed(() => model.value.history || []);

const moneyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

const statusLabel = status => {
  switch (status) {
    case 'done':
      return t('PROPERTY_OFFERS.CHECKLIST.STATUS.DONE');
    case 'waived':
      return t('PROPERTY_OFFERS.CHECKLIST.STATUS.WAIVED');
    case 'blocked':
      return t('PROPERTY_OFFERS.CHECKLIST.STATUS.BLOCKED');
    case 'in_progress':
      return t('PROPERTY_OFFERS.CHECKLIST.STATUS.IN_PROGRESS');
    default:
      return t('PROPERTY_OFFERS.CHECKLIST.STATUS.PENDING');
  }
};

const statusTone = status => {
  if (status === 'done') return 'positive';
  if (status === 'waived') return 'neutral';
  if (status === 'blocked') return 'danger';
  return 'warning';
};

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : dateFormatter.format(date);
};

const formatMoney = (amount, currency = 'BRL') => {
  const value = Number(amount || 0);
  if (currency === 'BRL') return moneyFormatter.format(value);
  return `${currency} ${value.toFixed(2)}`;
};

const waiverReasonLabel = reason =>
  t('PROPERTY_OFFERS.CHECKLIST.WAIVER_REASON_VALUE', { reason });

const actionContextLabel = action =>
  t('PROPERTY_OFFERS.CHECKLIST.ACTION_CONTEXT', {
    action: actionLabel(action.action),
    item: action.item.label,
  });

const installmentLabel = installment =>
  t('PROPERTY_OFFERS.CHECKLIST.INSTALLMENT_LABEL', {
    label: installment.label,
  });

const historyItemEventLabel = event =>
  t('PROPERTY_OFFERS.CHECKLIST.HISTORY_ITEM_EVENT', {
    item: event.itemSlug,
    event: event.eventType,
  });

const historyReasonLabel = reason =>
  t('PROPERTY_OFFERS.CHECKLIST.HISTORY_REASON_VALUE', { reason });

const isActionBusy = (item, action) =>
  props.actionInFlight === `${action}:${item.sourceId || item.id}`;

const requiresForm = action =>
  ['add_evidence', 'reopen', 'waive'].includes(action);

const openAction = (item, action) => {
  validationError.value = '';
  if (!action) return;
  if (!requiresForm(action)) {
    emit('itemAction', { item, action, payload: {} });
    return;
  }
  pendingAction.value = { item, action };
  actionReason.value = '';
  evidenceRef.value = '';
};

const handleAction = raw => {
  const source = items.value.find(item => item.id === raw.id);
  if (!source?.actionKey) return;
  openAction(source, source.actionKey);
};

const closeAction = () => {
  pendingAction.value = null;
  actionReason.value = '';
  evidenceRef.value = '';
  validationError.value = '';
};

const submitAction = () => {
  const action = pendingAction.value?.action;
  const item = pendingAction.value?.item;
  if (!action || !item) return;

  const reason = actionReason.value.trim();
  const evidence = evidenceRef.value.trim();
  if (action === 'waive' && !reason) {
    validationError.value = t('PROPERTY_OFFERS.CHECKLIST.ACTION_REQUIRED');
    return;
  }
  if (action === 'add_evidence' && !evidence) {
    validationError.value = t('PROPERTY_OFFERS.CHECKLIST.EVIDENCE_REQUIRED');
    return;
  }

  emit('itemAction', {
    item,
    action,
    payload: {
      reason,
      refs: evidence
        ? [{ kind: 'manual_reference', label: evidence, source: 'dashboard' }]
        : [],
    },
  });
  closeAction();
};
</script>

<template>
  <section class="offer-transaction-checklist flex w-full flex-col gap-3">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2
        class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        <span
          class="i-lucide-list-checks size-5 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
        {{ t('PROPERTY_OFFERS.CHECKLIST.TITLE') }}
      </h2>
      <div class="flex items-center gap-2">
        <span
          v-if="model.progress !== null && model.progress !== undefined"
          class="text-lais-body-sm font-semibold tabular-nums text-lais-text-body"
        >
          {{ progressLabel }}
        </span>
        <LaisStatusBadge
          v-if="hasTransaction && model.reference"
          :label="
            t('PROPERTY_OFFERS.CHECKLIST.REFERENCE', {
              reference: model.reference,
            })
          "
          tone="info"
          size="sm"
        />
        <LaisStatusBadge
          v-if="isPartial"
          :label="t('PROPERTY_OFFERS.CHECKLIST.PARTIAL')"
          tone="warning"
          size="sm"
        />
      </div>
    </div>

    <p
      v-if="isPartial"
      class="rounded-lais-sm bg-lais-bg-soft px-3 py-2 text-lais-body-xs text-lais-text-muted"
    >
      {{ t('PROPERTY_OFFERS.CHECKLIST.PARTIAL_HINT') }}
    </p>

    <LaisChecklistPanel
      :items="items"
      :loading="loading"
      :reference-now="referenceNow"
      :empty-label="t('PROPERTY_OFFERS.CHECKLIST.EMPTY')"
      @action="handleAction"
    />

    <p
      v-if="actionError"
      class="rounded-lais-sm border border-lais-danger/30 bg-lais-danger-50 px-3 py-2 text-lais-body-xs font-medium text-lais-danger"
    >
      {{ actionError }}
    </p>

    <div
      v-if="hasTransaction && actionItems.length"
      class="flex flex-col divide-y divide-lais-border-soft border-y border-lais-border-soft"
    >
      <div class="flex items-center justify-between gap-2 py-2">
        <h3 class="text-lais-label-md font-semibold text-lais-text">
          {{ t('PROPERTY_OFFERS.CHECKLIST.OPERATIONAL_TITLE') }}
        </h3>
        <span class="text-lais-label-xs text-lais-text-muted">
          {{
            t('PROPERTY_OFFERS.CHECKLIST.SUMMARY', {
              pending: model.summary?.pending_document_items || 0,
              blocked: model.summary?.blocked_items || 0,
              waived: model.summary?.waived_items || 0,
            })
          }}
        </span>
      </div>

      <article
        v-for="item in actionItems"
        :key="`operation-${item.id}`"
        class="offer-transaction-checklist__operation grid gap-3 py-3"
      >
        <div class="min-w-0 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="text-lais-body-sm font-semibold text-lais-text">
              {{ item.label }}
            </p>
            <LaisStatusBadge
              :label="statusLabel(item.rawStatus)"
              :tone="statusTone(item.rawStatus)"
              size="sm"
            />
            <span
              class="rounded-lais-sm bg-lais-bg-soft px-2 py-0.5 text-lais-label-xs text-lais-text-muted"
            >
              {{
                item.mandatory
                  ? t('PROPERTY_OFFERS.CHECKLIST.MANDATORY')
                  : t('PROPERTY_OFFERS.CHECKLIST.OPTIONAL')
              }}
            </span>
          </div>
          <dl
            class="flex flex-wrap gap-x-4 gap-y-1 text-lais-body-xs text-lais-text-muted"
          >
            <div v-if="item.owner" class="inline-flex gap-1">
              <dt>{{ t('PROPERTY_OFFERS.CHECKLIST.OWNER') }}</dt>
              <dd>{{ item.owner }}</dd>
            </div>
            <div v-if="item.deadline" class="inline-flex gap-1">
              <dt>{{ t('PROPERTY_OFFERS.CHECKLIST.DEADLINE') }}</dt>
              <dd>{{ formatDate(item.deadline) }}</dd>
            </div>
            <div v-if="item.completedAt" class="inline-flex gap-1">
              <dt>{{ t('PROPERTY_OFFERS.CHECKLIST.COMPLETED_AT') }}</dt>
              <dd>{{ formatDate(item.completedAt) }}</dd>
            </div>
            <div v-if="item.evidenceCount" class="inline-flex gap-1">
              <dt>{{ t('PROPERTY_OFFERS.CHECKLIST.EVIDENCE') }}</dt>
              <dd>{{ item.evidenceLabel }}</dd>
            </div>
            <div v-if="item.reopenedCount" class="inline-flex gap-1">
              <dt>{{ t('PROPERTY_OFFERS.CHECKLIST.REOPENED') }}</dt>
              <dd>{{ item.reopenedCount }}</dd>
            </div>
          </dl>
          <p
            v-if="item.waiver?.reason"
            class="text-lais-body-xs text-lais-text-muted"
          >
            {{ waiverReasonLabel(item.waiver.reason) }}
          </p>
        </div>

        <div
          class="offer-transaction-checklist__operation-actions flex flex-wrap items-start gap-2"
        >
          <button
            v-for="action in item.actions"
            :key="`${item.id}-${action}`"
            type="button"
            class="inline-flex min-h-8 items-center justify-center rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-3 py-1.5 text-lais-label-sm font-semibold text-lais-text-body transition-colors hover:border-lais-primary/50 hover:text-lais-text disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="isActionBusy(item, action)"
            @click="openAction(item, action)"
          >
            {{ actionLabel(action) }}
          </button>
        </div>
      </article>
    </div>

    <form
      v-if="pendingAction"
      class="flex flex-col gap-3 border border-lais-border-soft bg-lais-bg-soft p-3"
      @submit.prevent="submitAction"
    >
      <p class="text-lais-body-sm font-semibold text-lais-text">
        {{ actionContextLabel(pendingAction) }}
      </p>
      <label
        v-if="pendingAction.action === 'add_evidence'"
        class="flex flex-col gap-1 text-lais-label-sm font-medium text-lais-text-body"
      >
        {{ t('PROPERTY_OFFERS.CHECKLIST.ACTION_EVIDENCE') }}
        <input
          v-model="evidenceRef"
          class="min-h-10 rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :placeholder="
            t('PROPERTY_OFFERS.CHECKLIST.ACTION_EVIDENCE_PLACEHOLDER')
          "
        />
      </label>
      <label
        v-if="
          pendingAction.action === 'reopen' || pendingAction.action === 'waive'
        "
        class="flex flex-col gap-1 text-lais-label-sm font-medium text-lais-text-body"
      >
        {{ t('PROPERTY_OFFERS.CHECKLIST.ACTION_REASON') }}
        <textarea
          v-model="actionReason"
          rows="3"
          class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :placeholder="
            t('PROPERTY_OFFERS.CHECKLIST.ACTION_REASON_PLACEHOLDER')
          "
        />
      </label>
      <p
        v-if="validationError"
        class="text-lais-body-xs font-medium text-lais-danger"
      >
        {{ validationError }}
      </p>
      <div class="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          class="min-h-9 rounded-lais-sm border border-lais-border-soft px-3 text-lais-label-sm font-semibold text-lais-text-body"
          @click="closeAction"
        >
          {{ t('PROPERTY_OFFERS.CHECKLIST.ACTION_CANCEL') }}
        </button>
        <button
          type="submit"
          class="min-h-9 rounded-lais-sm bg-lais-primary px-3 text-lais-label-sm font-semibold text-white"
        >
          {{ t('PROPERTY_OFFERS.CHECKLIST.ACTION_CONFIRM') }}
        </button>
      </div>
    </form>

    <section
      v-if="hasTransaction && installments.length"
      class="flex flex-col gap-2 border-y border-lais-border-soft py-3"
    >
      <h3 class="text-lais-label-md font-semibold text-lais-text">
        {{ t('PROPERTY_OFFERS.CHECKLIST.INSTALLMENTS_TITLE') }}
      </h3>
      <div class="offer-transaction-checklist__action-fields grid gap-2">
        <div
          v-for="installment in installments"
          :key="`installment-${installment.id}`"
          class="grid gap-1 border border-lais-border-soft px-3 py-2 text-lais-body-xs"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="font-semibold text-lais-text">
              {{ installmentLabel(installment) }}
            </span>
            <LaisStatusBadge
              :label="installment.status"
              :tone="installment.settled ? 'positive' : 'warning'"
              size="sm"
            />
          </div>
          <p class="text-lais-text-body">
            {{ formatMoney(installment.amount, installment.currency) }}
          </p>
          <p class="text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.CHECKLIST.DEADLINE') }}
            {{ formatDate(installment.dueDate) }}
          </p>
          <p class="text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.CHECKLIST.BALANCE') }}
            {{ formatMoney(installment.balanceAmount, installment.currency) }}
          </p>
        </div>
      </div>
    </section>

    <section
      v-if="hasTransaction && history.length"
      class="flex flex-col gap-2 border-y border-lais-border-soft py-3"
    >
      <h3 class="text-lais-label-md font-semibold text-lais-text">
        {{ t('PROPERTY_OFFERS.CHECKLIST.HISTORY_TITLE') }}
      </h3>
      <ol class="space-y-2">
        <li
          v-for="event in history"
          :key="`history-${event.id}`"
          class="grid gap-1 text-lais-body-xs text-lais-text-muted"
        >
          <span class="font-semibold text-lais-text-body">
            {{ historyItemEventLabel(event) }}
          </span>
          <span class="flex flex-wrap gap-2">
            <span>{{ formatDate(event.occurredAt) }}</span>
            <span v-if="event.actor">{{ event.actor }}</span>
            <span v-if="event.hasEvidence">
              {{ t('PROPERTY_OFFERS.CHECKLIST.HISTORY_EVIDENCE') }}
            </span>
          </span>
          <span v-if="event.reason">
            {{ historyReasonLabel(event.reason) }}
          </span>
        </li>
      </ol>
    </section>
  </section>
</template>

<style scoped>
.offer-transaction-checklist {
  container-type: inline-size;
}

@container (min-width: 36rem) {
  .offer-transaction-checklist__action-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 44rem) {
  .offer-transaction-checklist__operation {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .offer-transaction-checklist__operation-actions {
    justify-content: flex-end;
  }
}
</style>
